import { useStoreList, getStoreColor, getStoreAbbreviation, type StorefrontEntry } from "@/hooks/useStoreList";

interface StoreBadgeProps {
  storefrontKey: string | null | undefined;
  /** Show the full store label instead of the 2-letter abbreviation. */
  showLabel?: boolean;
}

/**
 * Renders a small color dot + store identity for a storefront.
 * Default: 2-letter abbreviation. With `showLabel`: the full store name in a
 * soft pill (used on order cards to reveal which store a storefront order came
 * from). Returns null for null/undefined keys or unknown stores (graceful).
 */
export function StoreBadge({ storefrontKey, showLabel }: StoreBadgeProps) {
  const { data: stores } = useStoreList();

  if (!storefrontKey || !stores) return null;

  const store = stores.find((s: StorefrontEntry) => s.storefront_key === storefrontKey);
  if (!store) return null;

  const colorClass = getStoreColor(store.color_index);

  if (showLabel) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2 py-0.5 leading-none">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colorClass}`} />
        <span className="text-[11px] font-medium text-foreground/80">{store.label}</span>
      </span>
    );
  }

  const abbreviation = getStoreAbbreviation(store.label);
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colorClass}`} />
      <span className="text-[11px] text-muted-foreground leading-none">{abbreviation}</span>
    </span>
  );
}
